<template>
    <Card :header="$t('salesBySource')" overlay :loading="loading">
        <ErrorMessage v-if="error" :value="error" class="mb-2" @dismissed="error = null" />
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <DropdownFilter v-model="feature" :items="featureItems" :prefix="`${$t('type')}: `" :disabled="loading" />

                <Multiselect
                    :key="timeRangeString"
                    :fetch-options-function="fetchSources"
                    :placeholder="$t('sources')"
                    :selected-options-label="$t('selectedSources')"
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="sources = $event"
                    @loading="sourcesLoading = $event"
                />

                <Multiselect
                    v-if="!showProductGroupsResults"
                    :fetch-options-function="fetchProducerProducts"
                    :placeholder="$t('findProduct')"
                    :selected-options-label="$t('selectedProducts')"
                    preserve-search
                    asynchronous
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading && !spaceProductsLoading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="products = $event"
                    @loading="spaceProductsLoading = $event"
                />

                <Multiselect
                    v-if="showProductGroupsResults"
                    :key="showProductGroupsResults"
                    :fetch-options-function="fetchGroups"
                    :placeholder="$t('productGroups')"
                    :selected-options-label="$t('selectedProductGroups')"
                    preselect-all
                    :allow-empty="false"
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="groups = $event"
                    @loading="groupsLoading = $event"
                />

                <div class="w-100"></div>

                <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">
                    {{ $t('showProductGroupsResults') }}
                </b-form-checkbox>
            </div>
        </template>

        <div class="d-flex flex-wrap gap-4">
            <div style="flex: 3 1 300px">
                <EChart
                    :options="lineOptions"
                    type="time"
                    card
                    class="shadow-none"
                    :header="null"
                    :loading="loading"
                />
            </div>
            <div style="flex: 1 1 300px">
                <EChart
                    :options="pieOptions"
                    type="pie"
                    card
                    class="shadow-none"
                    :header="null"
                    :loading="loading"
                />
            </div>
        </div>
    </Card>
</template>

<script>
    import { sortBy } from 'lodash';
    import Card from '@/components/common/Card.vue';
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import salesReportMixin from '../salesReportMixin';
    import { fetchSources, fetchGroups } from '../utils';

    export default {
        name: 'SalesBySource',
        mixins: [salesReportMixin],
        components: { Card, EChart, DropdownFilter, Multiselect, ErrorMessage },
        data() {
            return {
                requestFeatureBySource: this.$req('reportsSales/FeatureBySourceChart'),
                requestFeatureBySource2: this.$req('reportsSales/FeatureBySourceChart'),
                requestSources: this.$req('reportsSales/UniqueSources'),
                requestProducerProducts: this.$req('space/ProducerProducts'),
                requestGroups: this.$req('producerProductGroups/SpaceGroups'),

                feature: 'amount',
                featureItems: ['amount', 'quantity', 'orders', 'widget_impressions', 'clicks', 'ctr', 'cr'],
                sources: [],
                products: [],
                productOptions: [],
                groups: [],

                sourcesLoading: false,
                spaceProductsLoading: false,
                groupsLoading: false,

                showProductGroupsResults: false,
                chart: [],
                pieChart: [],
                error: null,
            };
        },
        computed: {
            filtersLoading() {
                return this.sourcesLoading || this.spaceProductsLoading || this.groupsLoading;
            },
            loading() {
                return this.filtersLoading || this.requestFeatureBySource.getLoading() || this.requestFeatureBySource2.getLoading();
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate, feature: this.feature };
                if (this.sources?.length) requestParams.sources = sortBy(this.sources).join(',');
                if (this.showProductGroupsResults) {
                    if (this.groups?.length) requestParams.groups = sortBy(this.groups).join(',');
                } else {
                    const ids = this.selectedProductEntityIds;
                    if (ids?.length) requestParams.products = sortBy(ids).join(',');
                }
                return { urlParams, requestParams };
            },
            selectedProductEntityIds() {
                if (!Array.isArray(this.products) || !Array.isArray(this.productOptions)) return [];
                const byId = new Map(this.productOptions.map(product => [product.id, product]));
                return this.products.reduce((acc, id) => {
                    const item = byId.get(id);
                    const entityId = item?.entityId ?? item?.entity_id ?? item?.id ?? id;
                    if (entityId) acc.push(entityId);
                    return acc;
                }, []);
            },
            isCurrency() {
                return this.feature === 'amount';
            },
            isPercent() {
                return this.feature === 'ctr' || this.feature === 'cr';
            },
            isRatioFeature() {
                return this.feature === 'ctr' || this.feature === 'cr';
            },
            yAxisTitleMap() {
                return {
                    amount: this.$t('salesAmount'),
                    quantity: this.$t('quantity'),
                    orders: this.$t('salesSummary.transaction'),
                    widget_impressions: this.$t('impressions'),
                    clicks: this.$t('clicks'),
                    ctr: this.$t('ctr'),
                    cr: this.$t('cr'),
                };
            },
            yAxisTitle() {
                return this.yAxisTitleMap[this.feature] || '';
            },
            lineOptions() {
                if (!this.chart?.length) return { series: [] };
                const sourceKeys = Object.keys(this.chart[0]).filter(key => key !== 'date');
                const dates = this.chart.map(row => row.date);
                const series = sourceKeys.map(sourceKey => ({
                    name: sourceKey,
                    type: 'line',
                    smooth: true,
                    showSymbol: false,
                    data: this.chart.map(row => Number(row[sourceKey] ?? 0)),
                }));
                return {
                    tooltip: {
                        valueFormatter: value => {
                            if (this.isPercent) return this.$format.percent(value);
                            if (this.isCurrency) return this.$format.currency(value);
                            return this.$format.number(value, 0);
                        },
                    },
                    xAxis: {
                        data: dates,
                        axisLabel: { rotate: 45, interval: 0 },
                    },
                    yAxis: {
                        name: this.yAxisTitle,
                        min: 0,
                        axisLabel: {
                            formatter: value => {
                                if (this.isPercent) return this.$format.percent(value);
                                if (this.isCurrency) return this.$format.currency(value, null, null, 0);
                                return this.$format.number(value, 0);
                            },
                        },
                    },
                    series,
                };
            },
            totalValue() {
                if (this.isPercent) {
                    const values = (this.pieChart || []).map(item => Number(item.value || 0)).filter(val => !Number.isNaN(val) && val > 0);
                    if (values.length === 0) return 0;
                    const sum = values.reduce((acc, val) => acc + val, 0);
                    return sum / values.length;
                }
                return (this.pieChart || []).reduce((sum, item) => sum + Number(item.value || 0), 0);
            },
            pieOptions() {
                return {
                    title: {
                        text: `${this.$t('summary')}: ${this.formatTotalValue()}`,
                        textStyle: { fontWeight: 'bold', fontSize: 12 },
                    },
                    tooltip: {
                        valueFormatter: value => {
                            if (this.isPercent) return this.$format.percent(value);
                            if (this.isCurrency) return this.$format.currency(value);
                            return this.$format.number(value, 0);
                        },
                    },
                    series: [
                        {
                            type: 'pie',
                            data: (this.pieChart || []).map(item => ({ name: item.name, value: Number(item.value ?? 0) })),
                        },
                    ],
                };
            },
        },
        watch: {
            spaceId() {
                this.resetAndFetch();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) this.fetchData();
                },
            },
        },
        methods: {
            resetFilters() {
                this.feature = 'amount';
                this.showProductGroupsResults = false;
                this.resetAndFetch();
                this.fetchData();
            },
            async fetchSources({ search }) {
                return fetchSources({ requestSources: this.requestSources, spaceId: this.spaceId, timeRange: this.timeRange, search });
            },
            fetchProducerProducts({ search }) {
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = {
                    ...(search && { search }),
                    ...(startDate && { startDate }),
                    ...(endDate && { endDate }),
                };
                return this.requestProducerProducts.get({ urlParams: { spaceId: this.spaceId }, requestParams }).then(items => {
                    const arr = Array.isArray(items) ? items : items?.items || [];
                    // Merge into productOptions by id to keep a cache
                    const known = new Map(this.productOptions.map(product => [product.id, product]));
                    arr.forEach(product => {
                        if (product && product.id != null) known.set(product.id, product);
                    });
                    this.productOptions = Array.from(known.values());
                    return arr;
                });
            },
            fetchGroups({ search }) {
                return fetchGroups({ requestGroups: this.requestGroups, spaceId: this.spaceId, search });
            },
            async resetAndFetch() {
                this.sources = [];
                this.groups = [];
                this.products = [];
                this.chart = [];
                this.pieChart = [];
                this.requestFeatureBySource.abortAll?.();
                this.requestFeatureBySource.resetAll?.();
                this.requestFeatureBySource2.abortAll?.();
                this.requestFeatureBySource2.resetAll?.();
            },
            async fetchData() {
                if (!this.spaceId || !this.timeRange || this.filtersLoading) return;

                try {
                    this.error = null;

                    if (this.isRatioFeature) {
                        await this.fetchRatioData();
                    } else {
                        await this.fetchSimpleData();
                    }
                } catch (fetchError) {
                    this.error = fetchError;
                    this.chart = [];
                    this.pieChart = [];
                }
            },
            async fetchSimpleData() {
                const data = await this.requestFeatureBySource.get(this.apiParams);
                this.chart = Array.isArray(data?.chart) ? data.chart : [];
                this.pieChart = Array.isArray(data?.pie_chart) ? data.pie_chart.map(([sourceName, sourceValue]) => ({ name: sourceName, value: sourceValue })) : [];
            },

            async fetchRatioData() {
                const [numeratorFeature, denominatorFeature] = this.feature === 'ctr' ? ['clicks', 'widget_impressions'] : ['orders', 'clicks'];

                const baseParams = { ...this.apiParams };
                const numeratorParams = { ...baseParams, requestParams: { ...baseParams.requestParams, feature: numeratorFeature } };
                const denominatorParams = { ...baseParams, requestParams: { ...baseParams.requestParams, feature: denominatorFeature } };

                const [numeratorData, denominatorData] = await Promise.all([this.requestFeatureBySource.get(numeratorParams), this.requestFeatureBySource2.get(denominatorParams)]);

                this.chart = this.calculateRatioChart(numeratorData, denominatorData);
                this.pieChart = this.calculateRatioPie(numeratorData, denominatorData);
            },

            calculateRatioChart(numeratorData, denominatorData) {
                const numeratorChart = Array.isArray(numeratorData?.chart) ? numeratorData.chart : [];
                const denominatorChart = Array.isArray(denominatorData?.chart) ? denominatorData.chart : [];

                const denominatorByDate = new Map();
                for (const row of denominatorChart) {
                    const dateKey = row.date;
                    if (!denominatorByDate.has(dateKey)) {
                        denominatorByDate.set(dateKey, {});
                    }
                    const sourceKeys = Object.keys(row).filter(key => key !== 'date');
                    for (const sourceKey of sourceKeys) {
                        denominatorByDate.get(dateKey)[sourceKey] = Number(row[sourceKey] || 0);
                    }
                }

                return numeratorChart.map(row => {
                    const result = { date: row.date };
                    const denominatorRow = denominatorByDate.get(row.date) || {};
                    const sourceKeys = Object.keys(row).filter(key => key !== 'date');

                    for (const sourceKey of sourceKeys) {
                        const numeratorValue = Number(row[sourceKey] || 0);
                        const denominatorValue = Number(denominatorRow[sourceKey] || 0);
                        result[sourceKey] = denominatorValue > 0 ? (numeratorValue / denominatorValue) * 100 : 0;
                    }
                    return result;
                });
            },

            calculateRatioPie(numeratorData, denominatorData) {
                const numeratorPie = Array.isArray(numeratorData?.pie_chart) ? numeratorData.pie_chart : [];
                const denominatorPie = Array.isArray(denominatorData?.pie_chart) ? denominatorData.pie_chart : [];

                const denominatorBySource = new Map(denominatorPie.map(([sourceName, sourceValue]) => [sourceName, Number(sourceValue || 0)]));

                return numeratorPie.map(([sourceName, sourceValue]) => {
                    const numeratorValue = Number(sourceValue || 0);
                    const denominatorValue = denominatorBySource.get(sourceName) || 0;
                    const ratioValue = denominatorValue > 0 ? (numeratorValue / denominatorValue) * 100 : 0;
                    return { name: sourceName, value: ratioValue };
                });
            },
            formatTotalValue() {
                if (this.isPercent) {
                    return this.$format.percent(this.totalValue);
                }
                if (this.isCurrency) {
                    return this.$format.currency(this.totalValue);
                }
                return this.$format.number(this.totalValue, 0);
            },
        },
        created() {
            this.resetAndFetch();
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
</style>
